import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FiPlus, FiEdit2, FiTrash2, FiX, FiInfo, FiLoader, FiChevronDown, FiChevronRight, FiArrowLeft } from 'react-icons/fi';
import Modal from '../../components/Modal';
import DataTable from '../../components/DataTable';
import { questionnairesApi } from '../../services/api';

const answerTypeOptions = [
  { value: 'Single', label: 'Single' },
  { value: 'Multiple', label: 'Multiple' }
];

const initialQuestionFormState = {
  chooseQuestionnaire: '',
  answerType: 'Single',
  questionText: '',
  questionType: 'Text',
  questionGroup: '',
  controlIdentifier: '',
  instructions: '',
  answerOptions: []
};

const Questions = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const questionnaireId = searchParams.get('questionnaireId');
  const questionnaireName = decodeURIComponent(searchParams.get('name') || 'Questionnaire');
  const assessmentId = searchParams.get('assessmentId');

  // State
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [formData, setFormData] = useState(initialQuestionFormState);
  const [isEditing, setIsEditing] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);

  // New option input state
  const [newOptionValue, setNewOptionValue] = useState('');
  const [newOptionScore, setNewOptionScore] = useState(0);
  const [addingOption, setAddingOption] = useState(false);

  // Fetch questions from API
  const fetchQuestions = useCallback(async () => {
    if (!questionnaireId) {
      setError('No questionnaire ID provided');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await questionnairesApi.getQuestions(questionnaireId);
      setQuestions(data || []);
    } catch (err) {
      console.error('Error fetching questions:', err);
      setError(err.message || 'Failed to load questions');
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  }, [questionnaireId]);

  // Initial data fetch
  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  // Navigation
  const handleBackToQuestionnaires = () => {
    if (assessmentId) {
      navigate(`/questionnaires?assessmentId=${assessmentId}`);
    } else {
      navigate('/questionnaires');
    }
  };

  // Modal handlers
  const openCreateModal = () => {
    setFormData(initialQuestionFormState);
    setIsEditing(false);
    setIsModalOpen(true);
  };

  const openEditModal = (question) => {
    setFormData({
      questionText: question.questionText || '',
      questionType: question.questionType || 'Text',
      questionGroup: question.questionGroup || '',
      controlIdentifier: question.controlIdentifier || '',
      instructions: question.instructions || '',
      answerOptions: question.answerOptions?.map(opt => ({
        id: opt.id,
        value: opt.value,
        optionScore: opt.optionScore,
        displayOrder: opt.displayOrder
      })) || []
    });
    setSelectedQuestion(question);
    setIsEditing(true);
    setIsModalOpen(true);
  };

  const openDeleteModal = (question) => {
    setSelectedQuestion(question);
    setIsDeleteModalOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Answer Options management
  const addAnswerOption = () => {
    setFormData(prev => ({
      ...prev,
      answerOptions: [
        ...prev.answerOptions,
        {
          value: '',
          optionScore: 0,
          displayOrder: prev.answerOptions.length + 1
        }
      ]
    }));
  };

  const updateAnswerOption = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      answerOptions: prev.answerOptions.map((opt, i) =>
        i === index ? { ...opt, [field]: value } : opt
      )
    }));
  };

  const removeAnswerOption = (index) => {
    setFormData(prev => ({
      ...prev,
      answerOptions: prev.answerOptions.filter((_, i) => i !== index)
        .map((opt, i) => ({ ...opt, displayOrder: i + 1 }))
    }));
  };

  // Create or Update question via API
  const handleSubmit = async () => {
    if (!questionnaireId) {
      setError('No questionnaire ID provided');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const questionData = {
        questionnaireId: parseInt(questionnaireId),
        questionText: formData.questionText,
        questionType: formData.answerType || 'Single',
        questionGroup: formData.questionGroup || null,
        controlIdentifier: formData.controlIdentifier || null,
        instructions: formData.instructions || null,
        answerOptions: formData.answerOptions.map((opt, index) => ({
          value: opt.value,
          optionScore: parseInt(opt.optionScore) || 0,
          displayOrder: opt.displayOrder || index + 1
        }))
      };

      if (isEditing && selectedQuestion) {
        // Update existing question
        await questionnairesApi.updateQuestion(questionnaireId, selectedQuestion.questionId, questionData);
      } else {
        // Create new question
        await questionnairesApi.createQuestion(questionnaireId, questionData);
      }

      // Refresh the questions list
      await fetchQuestions();
      setIsModalOpen(false);
      setFormData(initialQuestionFormState);
    } catch (err) {
      console.error('Error saving question:', err);
      setError(err.message || 'Failed to save question');
    } finally {
      setSubmitting(false);
    }
  };

  // Delete question via API
  const handleDelete = async () => {
    if (!questionnaireId || !selectedQuestion) {
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      await questionnairesApi.deleteQuestion(questionnaireId, selectedQuestion.questionId);
      await fetchQuestions();
      setIsDeleteModalOpen(false);
      setSelectedQuestion(null);
    } catch (err) {
      console.error('Error deleting question:', err);
      setError(err.message || 'Failed to delete question');
    } finally {
      setSubmitting(false);
    }
  };

  // Add answer option
  const handleAddOption = async (questionId) => {
    if (!newOptionValue.trim()) {
      return;
    }

    try {
      setAddingOption(true);
      setError(null);
      await questionnairesApi.createAnswerOption(questionnaireId, questionId, {
        value: newOptionValue.trim(),
        optionScore: newOptionScore,
        displayOrder: null
      });
      await fetchQuestions();
      setNewOptionValue('');
      setNewOptionScore(0);
    } catch (err) {
      console.error('Error adding answer option:', err);
      setError(err.message || 'Failed to add answer option');
    } finally {
      setAddingOption(false);
    }
  };

  // Delete answer option
  const handleDeleteOption = async (questionId, optionId) => {
    try {
      setError(null);
      await questionnairesApi.deleteAnswerOption(questionnaireId, questionId, optionId);
      await fetchQuestions();
    } catch (err) {
      console.error('Error deleting answer option:', err);
      setError(err.message || 'Failed to delete answer option');
    }
  };

  const inputClass = `w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#ff6600] bg-white border-slate-200 text-slate-800`;
  const labelClass = `block text-sm font-medium mb-2 text-slate-700`;

  // DataTable column definitions
  const tableColumns = [
    {
      key: 'questionText',
      header: 'Question Text',
      sortable: true,
      filterable: true,
      render: (question) => (
        <div>
          <div className="flex items-start gap-2">
            <button
              onClick={(e) => { e.stopPropagation(); setExpandedRow(expandedRow === question.questionId ? null : question.questionId); }}
              className="mt-0.5 p-0.5 rounded hover:bg-slate-100 transition-colors text-slate-400 flex-shrink-0"
              title={expandedRow === question.questionId ? 'Collapse' : 'Expand answer options'}
            >
              {expandedRow === question.questionId ? <FiChevronDown className="w-4 h-4" /> : <FiChevronRight className="w-4 h-4" />}
            </button>
            <p className="text-sm font-medium text-slate-800">
              {question.questionText?.substring(0, 80)}{question.questionText?.length > 80 ? '...' : ''}
            </p>
          </div>
          {expandedRow === question.questionId && (
            <div className="mt-3 ml-6 border border-slate-200 rounded-lg p-4 bg-white">
              <div className="flex items-center gap-4 mb-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Enter New Option:</label>
                  <input
                    type="text"
                    value={newOptionValue}
                    onChange={(e) => setNewOptionValue(e.target.value)}
                    placeholder="Enter option text"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#ff6600] bg-white"
                  />
                </div>
                <div className="w-32">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Rating Score:</label>
                  <input
                    type="number"
                    value={newOptionScore}
                    onChange={(e) => setNewOptionScore(parseInt(e.target.value) || 0)}
                    min="0"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#ff6600] bg-white"
                  />
                </div>
                <div className="pt-6">
                  <button
                    onClick={() => handleAddOption(question.questionId)}
                    disabled={addingOption || !newOptionValue.trim()}
                    className="w-10 h-10 flex items-center justify-center rounded-lg border-2 border-[#ff6600] text-[#ff6600] hover:bg-[#ff6600] hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Add option"
                  >
                    <FiPlus className="text-lg" />
                  </button>
                </div>
              </div>
              {question.answerOptions && question.answerOptions.length > 0 && (
                <div className="space-y-2">
                  {question.answerOptions
                    .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
                    .map((option, optIndex) => (
                      <div key={option.id || optIndex} className="flex items-center gap-3">
                        {question.questionType === 'Single' ? (
                          <input type="radio" name={`q-${question.questionId}`} disabled className="w-4 h-4 accent-[#ff6600] border-slate-300" />
                        ) : question.questionType === 'Multiple' ? (
                          <input type="checkbox" disabled className="w-4 h-4 accent-[#ff6600] rounded border-slate-300" />
                        ) : (
                          <input type="radio" disabled className="w-4 h-4 accent-[#ff6600] border-slate-300" />
                        )}
                        <span className="text-sm text-slate-700">{option.value}</span>
                        <span className="text-sm text-slate-500">(</span>
                        <input type="number" value={option.optionScore} readOnly className="w-12 px-1 py-0.5 border border-slate-200 rounded text-sm text-center bg-slate-50" />
                        <span className="text-sm text-slate-500">)</span>
                        <button
                          onClick={() => handleDeleteOption(question.questionId, option.id)}
                          className="text-[#ff6600] hover:text-red-600 transition-colors ml-1"
                          title="Delete option"
                        >
                          <FiX className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                </div>
              )}
            </div>
          )}
        </div>
      )
    },
    {
      key: 'questionType',
      header: 'Answer Type',
      sortable: true,
      filterable: true,
      render: (q) => <span className="text-sm text-slate-600">{q.questionType || '-'}</span>
    },
    {
      key: 'controlIdentifier',
      header: 'Control ID',
      sortable: true,
      filterable: true,
      render: (q) => <span className="text-sm text-slate-600">{q.controlIdentifier || '-'}</span>
    },
    {
      key: 'questionGroup',
      header: 'Question Group',
      sortable: true,
      filterable: true,
      render: (q) => <span className="text-sm text-slate-600">{q.questionGroup || '-'}</span>
    }
  ];

  const tableActions = (question) => [
    { label: 'Edit', icon: FiEdit2, onClick: () => openEditModal(question) },
    { divider: true },
    { label: 'Delete', icon: FiTrash2, onClick: () => openDeleteModal(question), danger: true }
  ];

  return (
    <div className="space-y-4">
      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center justify-between">
          <span className="text-sm">{error}</span>
          <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">
            <FiX className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Questionnaire Context Banner */}
      {questionnaireId && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={handleBackToQuestionnaires}
              className="p-2 rounded-lg bg-white border border-blue-200 text-blue-600 hover:bg-blue-100 transition-colors"
              title="Back to Questionnaires"
            >
              <FiArrowLeft className="text-lg" />
            </button>
            <div>
              <p className="text-xs text-blue-600 font-medium uppercase tracking-wide">Questions for Questionnaire</p>
              <p className="text-lg font-semibold text-slate-800">{questionnaireName}</p>
            </div>
          </div>
          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
            ID: {questionnaireId}
          </span>
        </div>
      )}

      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-slate-800">Add or edit a question</h1>
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-[#ff6600] text-white">
            {questions.length} questions
          </span>
        </div>
        <nav className="flex items-center gap-1.5 text-sm">
          <button onClick={() => navigate('/assessments')} className="text-[#0b79b8] hover:underline font-medium">Assessments</button>
          <span className="text-slate-400">&gt;</span>
          <button onClick={handleBackToQuestionnaires} className="text-[#0b79b8] hover:underline font-medium">Questionnaires</button>
          <span className="text-slate-400">&gt;</span>
          <span className="text-slate-600 font-medium">Questions</span>
        </nav>
      </div>

            {
        loading && (  
           <div className="mt-4 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="w-80 h-9 bg-[#e6eef5] rounded animate-pulse mb-2" />
              
            </div>
            <div className="flex items-center justify-end gap-2">
              <div className="w-96 h-10 bg-[#e6eef5] rounded animate-pulse" />
           
            </div>
          </div>

         
        </div>
        )
      }

      {/* Data Table */}
      <DataTable
        columns={tableColumns}
        data={questions}
        rowKey="questionId"
        loading={loading}
        error={error}
        onRetry={fetchQuestions}
        searchable={true}
        searchPlaceholder="Search questions..."
        actions={tableActions}
        headerActions={
          <button
            onClick={openCreateModal}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#ff6600] text-white rounded-lg text-sm font-medium hover:bg-[#e65c00] transition-colors shadow-sm"
          >
            <FiPlus className="text-base" /> Add Question
          </button>
        }
        emptyMessage="No questions found"
        totalLabel="questions"
      />

      {/* Add / Edit Question Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title=""
        size="lg"
      >
        <div className="space-y-6">
          {/* Modal Title */}
          <h2 className="text-xl font-bold text-[#0b79b8]">
            {isEditing ? 'Edit Question' : 'Add Question'}
          </h2>

          {/* Row 1: Choose Questionnaire & Answer Type */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Questionnaire (Read-only) */}
            <div>
              <label className="block text-sm font-semibold mb-2 text-slate-800">
                Questionnaire
              </label>
              <input
                type="text"
                value={questionnaireName}
                readOnly
                className="w-full px-4 py-2.5 border rounded-lg text-sm bg-slate-50 border-slate-200 text-slate-600 cursor-not-allowed focus:outline-none"
              />
            </div>

            {/* Answer Type Dropdown */}
            <div>
              <label className="block text-sm font-semibold mb-2 text-slate-800">
                Answer Type <FiInfo className="inline w-3.5 h-3.5 text-[#0b79b8] ml-1 -mt-0.5" />
              </label>
              <select
                name="answerType"
                value={formData.answerType}
                onChange={handleInputChange}
                className={inputClass}
              >
                <option value="" disabled>Select answer type</option>
                {answerTypeOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Row 2: Question Text */}
          <div>
            <label className="block text-sm font-semibold mb-2 text-slate-800">Question Text</label>
            <textarea
              name="questionText"
              value={formData.questionText}
              onChange={handleInputChange}
              className={inputClass}
              rows={4}
              placeholder="Select Category"
            />
          </div>

          {/* Row 3: Instructions */}
          <div>
            <label className="block text-sm font-semibold mb-2 text-slate-800">Instructions</label>
            <textarea
              name="instructions"
              value={formData.instructions}
              onChange={handleInputChange}
              className={inputClass}
              rows={4}
              placeholder="Describe the custom control, e.g., internal encryption key rotation policy beyond standard requirements."
            />
          </div>

          {/* Row 4: Control Id & Group */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-sm font-semibold mb-2 text-slate-800">Control Id</label>
              <input
                type="text"
                name="controlIdentifier"
                value={formData.controlIdentifier}
                onChange={handleInputChange}
                className={inputClass}
                placeholder="e.g., AC-001"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-2 text-slate-800">Group</label>
              <input
                type="text"
                name="questionGroup"
                value={formData.questionGroup}
                onChange={handleInputChange}
                className={inputClass}
                placeholder=""
              />
            </div>
          </div>

          {/* Footer Buttons */}
          <div className="flex items-center justify-center gap-4 pt-4">
            <button
              onClick={handleSubmit}
              disabled={submitting || !formData.questionText.trim()}
              className="px-8 py-2.5 bg-[#ff6600] text-white rounded-full text-sm font-medium hover:bg-[#e65c00] transition-colors shadow-sm min-w-[120px] disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
            >
              {submitting && <FiLoader className="w-4 h-4 animate-spin" />}
              {submitting ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={() => setIsModalOpen(false)}
              disabled={submitting}
              className="px-8 py-2.5 bg-[#0b79b8] text-white rounded-full text-sm font-medium hover:bg-[#0968a0] transition-colors shadow-sm min-w-[120px] disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>

      {/* ── Delete Question Modal ── */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => !submitting && setIsDeleteModalOpen(false)}
        title="Delete Question"
        size="sm"
        footer={
          <>
            <button
              onClick={() => setIsDeleteModalOpen(false)}
              disabled={submitting}
              className="px-4 py-2 border rounded-lg text-sm transition-colors border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              disabled={submitting}
              className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-2"
            >
              {submitting && <FiLoader className="w-4 h-4 animate-spin" />}
              {submitting ? 'Deleting...' : 'Delete'}
            </button>
          </>
        }
      >
        <div className="text-center py-4">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FiTrash2 className="text-red-600 text-2xl" />
          </div>
          <h3 className="text-lg font-medium mb-2 text-slate-800">Are you sure?</h3>
          <p className="text-slate-500">
            You are about to delete this question and all its answer options.
            <br />This action cannot be undone.
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default Questions;
